import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HttpBaseService } from 'src/app/base/http-base.service';
import ScreenInterimActions from '../../actions/generations/screen-interim.actions';
import { catchError, map, of, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';

@Injectable()
export class ScreenInterimEffects {
  private actions$ = inject(Actions);
  private baseService = inject(HttpBaseService);
  private store = inject(Store);

  private selectedScreensInterim: any[];

  getAllScreensData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScreenInterimActions.getScreensInterim),
      switchMap((action: any) =>
        this.baseService[action.getScreensInterimApiConstants?.method](
          action.getScreensInterimApiConstants,
          action.payloadWithPathVariables
        ).pipe(
          map((screensData: any) => {
            const screens = screensData?.results ?? screensData;
            this.selectedScreensInterim = screens;
            return ScreenInterimActions.setScreensInterim({
              screensInterim: screens,
            });
          }),
          catchError((error) => {
            console.error('Error fetching interim screens:', error);
            return of(ScreenInterimActions.setScreensInterim({
              screensInterim: [],
            }));
          })
        )
      )
    )
  );

  getScreenStructureInterim$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ScreenInterimActions.getScreenStructureInterim),
        switchMap((action: any) =>
          this.baseService[action.getScreenStructureInterimApiConstants?.method](
            action.getScreenStructureInterimApiConstants,
            action.sid
          ).pipe(
            map((screenData: any) => {
              this.store.dispatch(
                ScreenInterimActions.setScreenStructureInterim({
                  screenStructureInterim: screenData || null,
                })
              );
            }),
            catchError((error) => {
              console.error('Error fetching screen structure interim:', error);
              // Dispatch an empty object (not null) so the selector emits even if
              // the store was already null — prevents stuck loaders caused by
              // distinctUntilChanged suppressing null → null transitions
              this.store.dispatch(
                ScreenInterimActions.setScreenStructureInterim({
                  screenStructureInterim: { _error: true },
                })
              );
              return of(null);
            })
          )
        )
      ),
    {
      dispatch: false,
    }
  );

  updateScreenStructureInterim$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ScreenInterimActions.updateScreenStructureInterim),
        map((action: any) => {
          if (!action.updateStructureInterimApiConstants) {
            return;
          }
          this.store.dispatch(ScreenInterimActions.saveConfFlCntSuccessInterim({ results: null }));
          this.store.dispatch(ScreenInterimActions.saveConfFlCntFailureInterim({ error: null }));
          return this.baseService[action.updateStructureInterimApiConstants?.method](
            action.updateStructureInterimApiConstants,
            action.updatedStructure
          ).subscribe(
            (screenData: any) => {
              const scr = this.selectedScreensInterim?.find((sc) => sc.sid === screenData.sid);
              if (scr) {
                const updScrs = this.selectedScreensInterim.map((sc) => {
                  return sc.sid === scr.sid ? screenData : sc;
                });
                this.selectedScreensInterim = [...updScrs];
              }
              this.store.dispatch(ScreenInterimActions.setScreensInterim({ screensInterim: this.selectedScreensInterim }));
            },
            (err: any) => {
              this.store.dispatch(ScreenInterimActions.saveConfFlCntFailureInterim({ error: err }));
            }
          );
        })
      ),
    {
      dispatch: false,
    }
  );

  updateScnAndDMConfigInterim$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ScreenInterimActions.updateScnAndDMConfigInterim),
        map((action: any) => {
          if (!action.updateApiCons) {
            return;
          }
          this.store.dispatch(ScreenInterimActions.saveConfFlCntSuccessInterim({ results: null }));
          this.store.dispatch(ScreenInterimActions.saveConfFlCntFailureInterim({ error: null }));
          return this.baseService[action.updateApiCons?.method](action.updateApiCons, action.updatedData).subscribe(
            (screenData: any) => {
              // Update local cache so screens list reflects the saved changes immediately
              const scr = this.selectedScreensInterim?.find((sc) => sc.sid === screenData.sid);
              if (scr) {
                const updScrs = this.selectedScreensInterim.map((sc) => {
                  return sc.sid === scr.sid ? screenData : sc;
                });
                this.selectedScreensInterim = [...updScrs];
              }
              this.store.dispatch(ScreenInterimActions.setScreensInterim({ screensInterim: this.selectedScreensInterim }));
              this.store.dispatch(ScreenInterimActions.saveConfFlCntSuccessInterim({ results: screenData }));
            },
            (err: any) => {
              this.store.dispatch(ScreenInterimActions.saveConfFlCntFailureInterim({ error: err }));
            }
          );
        })
      ),
    {
      dispatch: false
    }
  );

  deleteScreenInterim$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ScreenInterimActions.deleteScreenInterim),
        map((action: any) => {
          return this.baseService[action.deleteScreenInterimApi?.method](action.deleteScreenInterimApi, action.screenId).subscribe(
            (screensData: any) => {
              this.store.dispatch(ScreenInterimActions.currentDeleteScreenInterim({ deletedScreenInterimId: action.screenId }));
              const ind = this.selectedScreensInterim?.findIndex((sc) => sc.sid === action.screenId?.screenId);
              if (ind > -1) {
                const allScs = [...this.selectedScreensInterim];
                allScs.splice(ind, 1);
                this.selectedScreensInterim = [...allScs];
                this.store.dispatch(ScreenInterimActions.setScreensInterim({ screensInterim: this.selectedScreensInterim }));
              }
            },
            (error: any) => {
              console.error('Error deleting screen interim:', error);
              this.store.dispatch(ScreenInterimActions.deleteScreenInterimFailure({ screenId: action.screenId, error }));
            }
          );
        })
      ),
    {
      dispatch: false
    }
  );

}
